import { ClearFiltersButton, FilterSelect, SearchInput } from '@/shared/components/Filters';
import { Pagination } from '@/shared/components/Pagination';
import { ValidationErrorDisplay } from '@/shared/components/ValidationErrorDisplay';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { useScrollToErrorAlert } from '@/shared/hooks/useScrollToErrorAlert';
import type { EntityFilters } from '@/shared/types/entity';
import { toFilterString } from '@/shared/utils/string';
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormLabel,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { EntityListColumn, EntityListProps } from './types';

dayjs.extend(utc);

export const EntityList = <T extends { id: number }, F extends object>({
  title,
  items,
  error,
  currentPage,
  perPage,
  totalPages,
  total,
  columns,
  filters,
  filterConfigs,
  createEndpoint,
  updateEndpoint,
  onDelete,
  onPageChange,
  onPerPageChange,
  onFiltersChange,
  onClearFilters,
  isFiltersEmpty,
  requiredViewPermission,
  requiredManagePermission,
  createButtonDisabled = false,
}: EntityListProps<T, F>) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { errorAlertRef, isErrorVisible, setIsErrorVisible } = useScrollToErrorAlert(error);
  const entityFilters = filters as EntityFilters;

  const canView = requiredViewPermission ? hasPermission(requiredViewPermission) : true;
  const canManage = requiredManagePermission ? hasPermission(requiredManagePermission) : true;

  const handleCreate = () => {
    void navigate(createEndpoint.replace('/admin', ''));
  };

  const handleEdit = (id: number) => {
    void navigate(updateEndpoint(id.toString()).replace('/admin', ''));
  };

  const handleDateRangeChange = useCallback(
    (field: 'start' | 'end', value: dayjs.Dayjs | null) => {
      const currentRange = entityFilters.dateRange || { start: null, end: null };
      onFiltersChange({
        dateRange: {
          ...currentRange,
          [field]: value ? value.format('YYYY-MM') : null,
        },
      } as unknown as Partial<F>);
    },
    [entityFilters.dateRange, onFiltersChange],
  );

  const renderFilters = () => (
    <CRow className="mb-3 gy-3">
      {filterConfigs.map((config) => {
        const selectId = `entity-list-filter-${config.key}`;

        return (
          <CCol
            key={config.key}
            xs={12}
            sm={config.sm}
            md={config.md ?? 4}
            lg={config.lg}
            xl={config.xl}
            className={config.className}
          >
            {config.type === 'search' && (
              <SearchInput
                value={toFilterString((filters as Record<string, unknown>)[config.key])}
                onChange={(value) => onFiltersChange({ [config.key]: value } as Partial<F>)}
                placeholder={config.placeholder || 'Search...'}
              />
            )}
            {config.type === 'select' && config.options && (
              <>
                {config.label ? <CFormLabel htmlFor={selectId}>{config.label}</CFormLabel> : null}
                <FilterSelect
                  id={selectId}
                  value={
                    toFilterString((filters as Record<string, unknown>)[config.key]) ||
                    config.options[0]?.value ||
                    ''
                  }
                  onChange={(e) => onFiltersChange({ [config.key]: e.target.value } as Partial<F>)}
                  options={config.options}
                />
              </>
            )}
            {config.type === 'dateRange' && (
              <CRow>
                <CCol md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={
                        entityFilters.dateRange?.start
                          ? dayjs.utc(entityFilters.dateRange.start + '-01')
                          : null
                      }
                      onChange={(date) => handleDateRangeChange('start', date)}
                      format="MM/YYYY"
                      views={['month', 'year']}
                      timezone="UTC"
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          slotProps: {
                            htmlInput: {
                              placeholder: 'Select start month',
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </CCol>
                <CCol md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={
                        entityFilters.dateRange?.end
                          ? dayjs.utc(entityFilters.dateRange.end + '-01')
                          : null
                      }
                      onChange={(date) => handleDateRangeChange('end', date)}
                      format="MM/YYYY"
                      views={['month', 'year']}
                      timezone="UTC"
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          slotProps: {
                            htmlInput: {
                              placeholder: 'Select end month',
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </CCol>
              </CRow>
            )}
          </CCol>
        );
      })}
    </CRow>
  );

  const renderCellContent = (item: T, column: EntityListColumn<T>) => {
    if (column.render) {
      return column.render(item);
    }

    if (column.key === 'actions') {
      if (!canManage) return null;
      return (
        <>
          <CButton color="primary" size="sm" className="me-2" onClick={() => handleEdit(item.id)}>
            Edit
          </CButton>
          <CButton color="danger" size="sm" onClick={() => onDelete(item.id)}>
            Delete
          </CButton>
        </>
      );
    }

    const value = item[column.key];
    return value ? String(value) : '-';
  };

  if (!canView) {
    return (
      <CCard>
        <CCardHeader>
          <h4 className="mb-0">{title}</h4>
        </CCardHeader>
        <CCardBody>
          <CAlert color="danger">You don't have permission to view this content.</CAlert>
        </CCardBody>
      </CCard>
    );
  }

  return (
    <CCard>
      <CCardHeader className="d-flex justify-content-between align-items-center">
        <h4 className="mb-0">{title}</h4>
        {createEndpoint && canManage && (
          <CButton color="primary" onClick={handleCreate} disabled={createButtonDisabled}>
            Create New
          </CButton>
        )}
      </CCardHeader>

      <CCardBody>
        {error && isErrorVisible && (
          <div ref={errorAlertRef}>
            <CAlert color="danger" dismissible onClose={() => setIsErrorVisible(false)}>
              {(() => {
                const message =
                  typeof error === 'string'
                    ? error
                    : typeof error === 'object' &&
                        error !== null &&
                        'status' in error &&
                        error.status === 401
                      ? 'You do not have permission to perform this action. Please contact your administrator.'
                      : typeof error === 'object' && error !== null && 'message' in error
                        ? (error as { message: string }).message
                        : 'An error occurred';
                return <ValidationErrorDisplay message={message} />;
              })()}
            </CAlert>
          </div>
        )}

        {/* Filters */}
        <div className="mb-4">{renderFilters()}</div>

        {/* Table */}
        <CTable hover responsive>
          <CTableHead>
            <CTableRow>
              {columns.map((column) => (
                <CTableHeaderCell
                  key={String(column.key)}
                  className={column.align ? `text-${column.align}` : ''}
                >
                  {column.header}
                </CTableHeaderCell>
              ))}
            </CTableRow>
          </CTableHead>
          <CTableBody>
            {!items || items.length === 0 ? (
              <CTableRow>
                <CTableDataCell colSpan={columns.length} className="text-center">
                  No items found
                </CTableDataCell>
              </CTableRow>
            ) : (
              items.map((item) => (
                <CTableRow key={item.id}>
                  {columns.map((column) => (
                    <CTableDataCell
                      key={String(column.key)}
                      className={column.align ? `text-${column.align}` : ''}
                    >
                      {renderCellContent(item, column)}
                    </CTableDataCell>
                  ))}
                </CTableRow>
              ))
            )}
          </CTableBody>
        </CTable>

        {!isFiltersEmpty(filters) && (
          <CRow className="mb-3">
            <CCol md={3}>
              <ClearFiltersButton onClick={onClearFilters} />
            </CCol>
          </CRow>
        )}

        {/* Pagination */}
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            perPage={perPage}
            total={total}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        </div>
      </CCardBody>
    </CCard>
  );
};

export type { EntityListColumn } from './types';
